import {ReadinessHeading, readinessCard, readinessPaymentButton} from "./ReadinessPresentation";
import React, { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { CheckCircle2, AlertTriangle, Circle, Send, ShieldCheck, RefreshCw, FileText, PoundSterling } from "lucide-react";
import { staffApi } from "@/lib/staffApi";
import { formatMoney } from "@/lib/format";
import { SendMedicalFormModal } from "@/components/SendMedicalFormModal";
import { RecordPaymentModal } from "@/components/RecordPaymentModal";
import { CustomerSubmissionModal } from "@/components/CustomerSubmissionModal";

/**
 * ReadinessPanel — the single-glance "ready for treatment?" summary for
 * Angela on the Appointment Detail page. Aggregates deposit, consultation,
 * medical-form, outstanding balance, and pending clinical flags.
 */
export function ReadinessPanel({ appointment, onPaymentRecorded, clinicalOnly = false }) {
    const [state, setState] = useState(null);
    const [modalOpen, setModalOpen] = useState(false);
    const [paymentOpen, setPaymentOpen] = useState(false);
    const [sendKind, setSendKind] = useState("medical");
    const [submissions, setSubmissions] = useState([]);
    const [formsError, setFormsError] = useState('');
    const [viewForm, setViewForm] = useState(null);

    const load = useCallback(() => {
        setFormsError('');
        staffApi.listAppointmentSubmissions(appointment.id).then(r => setSubmissions(r.submissions || [])).catch(() => setFormsError('Could not load completed forms. Please refresh.'));
        staffApi.appointmentReadiness(appointment.id)
            .then(setState)
            .catch(() => setState({ error: true }));
    }, [appointment.id]);
    useEffect(load, [load]);

    const completedForms = <>
            <div className="border-t border-white/15 pt-3 space-y-2" data-testid="appointment-completed-forms">
                <h3 className="text-sm text-white font-semibold">Completed forms for this appointment</h3>
                {formsError ? <p role="alert" className="text-sm text-amber-200">{formsError}</p> : submissions.length === 0 ? <p className="text-xs text-white/60">No completed forms received for this appointment yet.</p> : submissions.map(form => <div key={form.id} className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-white/5 p-3">
                    <div className="min-w-0 flex-1 text-sm text-white"><div>{form.title_snapshot}</div><div className="text-xs text-white/60">Completed {new Date(form.submitted_at).toLocaleString('en-GB')}</div></div>
                    <button type="button" onClick={() => setViewForm(form.id)} className="rounded-full bg-gold text-navy px-3 py-2 text-xs font-semibold">View form</button>
                </div>)}
            </div>
            {viewForm && <CustomerSubmissionModal id={viewForm} onClose={() => setViewForm(null)} />}
    </>;

    if (!state) return <div className="rounded-2xl glass p-4 text-xs text-white/50">Loading readiness…</div>;
    if (state.error) return <section className="rounded-2xl glass p-4"><div role="alert" className="text-amber-200">Could not verify appointment readiness. <button onClick={load} className="underline">Retry</button></div>{completedForms}</section>;

    const mf = state.medical_form;
    const mfStatus = mf ? mf.status : "not_sent";
    const flags = mf?.attention_flags || [];
    const reviewedNeeded = flags.length > 0 && !mf?.reviewed_at;
    const consultForms = submissions.filter(s => ['consultation', 'general_consultation', 'treatment_consultation'].includes(s.form_kind));
    const medicalForms = submissions.filter(s => ['medical', 'treatment_consent'].includes(s.form_kind));
    const consultDone = state.consultation?.applicable === undefined ? state.consultation.completed || consultForms.length > 0 : state.consultation.completed;
    const medicalDone = mf?.applicable === undefined ? mfStatus === 'completed' || mfStatus === 'reviewed' || medicalForms.length > 0 : mf.completed;

    const rows = [
        {
            key: "deposit", label: "Deposit",
            done: state.deposit.paid && state.deposit.satisfied,
            hint: state.deposit.paid_amount > 0
                ? `${formatMoney(state.deposit.paid_amount)} paid${!state.deposit.satisfied ? ` · ${formatMoney(state.deposit.required_amount)} deposit required` : ""}`
                : state.deposit.required_amount <= 0 ? "No deposit required" : "Not paid",
            icon: PoundSterling,
        },
        {
            key: "consult", label: "Consultation Form",
            done: consultDone,
            hint: consultDone ? "Completed — view below" : mfStatusLabel(state.consultation?.status || 'not_sent', false),
            icon: FileText,
        },
        {
            key: "medical", label: "Medical Form",
            done: medicalDone,
            attn: mfStatus === "requires_attention" || reviewedNeeded,
            hint: mfStatusLabel(mfStatus, reviewedNeeded),
            icon: ShieldCheck,
            action: medicalDone ? null : ["not_sent", "sent", "opened", "incomplete", "prepared", "send_failed"].includes(mfStatus) ? "send" : (reviewedNeeded ? "review" : null),
            dispatchId: mf?.id,
        },
        {
            key: "outstanding", label: "Outstanding balance",
            done: state.deposit.outstanding <= 0.01,
            hint: state.deposit.outstanding > 0.01 ? `${formatMoney(state.deposit.outstanding)} due at appointment` : "Nothing owed",
            icon: PoundSterling,
        },
    ];

    const markReviewed = async () => {
        if (!mf?.id) return;
        try {
            await staffApi.markMedicalReviewed(appointment.id, mf.id);
            toast.success("Medical form marked as reviewed.");
            load();
        } catch (e) { toast.error(e.message || "Could not mark reviewed."); }
    };

    const prepareConsultation = () => { setSendKind("consultation"); setModalOpen(true); };

    return (
        <section className={readinessCard} data-testid="readiness-panel">
            <ReadinessHeading label={state.outcome_label ? 'Appointment outcome' : 'Readiness'}
                title={clinicalOnly ? 'Treatment forms and review' : state.outcome_label ? 'Treatment record' : 'Ready for treatment?'}
                verdict={clinicalOnly ? null : state.outcome_label || (state.ready_for_treatment ? 'Yes' : 'Action required')}
                ready={state.outcome_label === 'Completed' || (!state.outcome_label && state.ready_for_treatment)}/>


            {reviewedNeeded && (
                <div className="rounded-xl border border-amber-400/50 bg-amber-500/10 p-3 flex items-start gap-2 text-amber-100 text-sm" data-testid="readiness-attention">
                    <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <div className="font-medium">Requires attention</div>
                        <ul className="mt-1 text-xs list-disc list-inside space-y-0.5">
                            {flags.map((f, i) => <li key={i}>{f}</li>)}
                        </ul>
                    </div>
                    <button type="button" onClick={markReviewed}
                            data-testid="readiness-mark-reviewed"
                            className="rounded-full bg-emerald-500/20 border border-emerald-400/50 text-emerald-100 px-3 h-8 text-xs font-semibold whitespace-nowrap">
                        Mark reviewed
                    </button>
                </div>
            )}

            <ul className="divide-y divide-white/5">
                {rows.filter(r => !clinicalOnly || !['deposit','outstanding'].includes(r.key)).filter(r => r.key !== "consult" || state.consultation?.applicable !== false).filter(r => r.key !== "medical" || mf?.applicable !== false).map((r) => (
                    <li key={r.key} className="py-2.5 flex flex-wrap items-center gap-3" data-testid={`readiness-row-${r.key}`}>
                        <div className={`h-8 w-8 rounded-full grid place-items-center shrink-0
                            ${r.attn ? "bg-amber-500/15 text-amber-200" : r.done ? "bg-emerald-500/15 text-emerald-200" : "bg-white/5 text-white/40"}`}>
                            {r.attn ? <AlertTriangle className="h-4 w-4" /> : r.done ? <CheckCircle2 className="h-4 w-4" /> : <Circle className="h-4 w-4" />}
                        </div>
                        <div className="flex-1 min-w-[120px]">
                            <div className="text-sm text-white break-words">{r.label}</div>
                            <div className="text-[11px] text-white/50 break-words">{r.hint}</div>
                            {["medical","consult"].includes(r.key) && <div className="text-[11px] text-white/50">{deliveryLabel((r.key==="medical"?mf:state.consultation)?.delivery_triggers)}</div>}
                        </div>
                        {r.action === "send" && (
                            <button type="button" onClick={() => {setSendKind("medical");setModalOpen(true);}}
                                    data-testid="readiness-send-medical"
                                    className="rounded-full bg-gold text-navy text-xs font-semibold px-3 h-8 flex items-center gap-1 whitespace-nowrap">
                                <Send className="h-3 w-3" /> Send Medical Form
                            </button>
                        )}
                        {r.key === "deposit" && state.deposit.outstanding > 0 && <button type="button" onClick={()=>setPaymentOpen(true)} className={readinessPaymentButton}>Record payment</button>}
                        {r.key === "consult" && !r.done && <button type="button" onClick={prepareConsultation} data-testid="readiness-prepare-consultation" className="rounded-full bg-gold text-navy text-xs font-semibold px-3 py-2 disabled:opacity-50">Send Consultation Form</button>}
                    </li>
                ))}
            </ul>

            {state.aftercare?.applicable && <div className="py-3 border-t border-white/10" data-testid="readiness-aftercare"><div className="flex flex-wrap items-center justify-between gap-3"><div><div className="text-sm text-white">Aftercare</div><p className="text-xs text-white/60">{state.aftercare.sent ? 'Aftercare already sent' : mfStatusLabel(state.aftercare.status,false)}</p><p className="text-xs text-white/50">{deliveryLabel(state.aftercare.delivery_triggers)}</p></div><button type="button" className="rounded-full bg-gold text-navy text-xs font-semibold px-3 py-2" onClick={()=>{if(state.aftercare.sent){toast.info('Aftercare already sent');return;}if(state.aftercare.status==='queued'){toast.info('Aftercare already queued to send');return;}setSendKind('aftercare');setModalOpen(true);}}>{state.aftercare.sent?'Aftercare already sent':state.aftercare.status==='queued'?'Aftercare queued':'Send Aftercare'}</button></div></div>}
            {completedForms}


            <div className="flex justify-between items-center pt-1">
                <button type="button" onClick={load} data-testid="readiness-refresh"
                        className="text-[11px] text-white/50 hover:text-white flex items-center gap-1">
                    <RefreshCw className="h-3 w-3" /> Refresh
                </button>
                {mf?.link && (
                    <a href={mf.link} target="_blank" rel="noopener noreferrer"
                       className="text-[11px] text-gold hover:underline" data-testid="readiness-open-form-link">
                        Open form link →
                    </a>
                )}
            </div>

            {modalOpen && (
                <SendMedicalFormModal
                    appointment={appointment}
                    formKind={sendKind}
                    onClose={() => { setModalOpen(false); load(); }}
                    onSent={() => { setModalOpen(false); load(); }}
                />
            )}
            {paymentOpen && <RecordPaymentModal appointmentId={appointment.id} outstanding={state.deposit.outstanding} onClose={()=>setPaymentOpen(false)} onSaved={()=>{setPaymentOpen(false);load();onPaymentRecorded?.();}}/>}
        </section>
    );
}

function mfStatusLabel(s, reviewedNeeded) {
    if (reviewedNeeded) return "Awaiting practitioner review";
    switch (s) {
        case "not_sent":            return "Not sent";
        case "incomplete":          return "Some required forms still outstanding";
        case "prepared":            return "Ready for customer to complete";
        case "queued":              return "Queued — waiting to send";
        case "send_failed":         return "Not delivered — please send again";
        case "sent":                return "Sent — awaiting customer";
        case "opened":              return "Opened by customer";
        case "requires_attention":  return "Completed — requires attention";
        case "completed":           return "Completed";
        case "reviewed":            return "Reviewed";
        default:                    return s || "Not sent";
    }
}

function deliveryLabel(triggers=[]) {
    return triggers.map(t=>t==='checkout'?'Automatic after checkout':t==='booking'?'Automatic after booking':'Manual sending').join(' · ');
}
